import { useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { api } from '../../lib/api'
import { downloadFile } from '../../lib/downloadFile'
import { useAuthStore } from '../../stores/auth'
import { Button } from '../../components/ui/Button'
import { Pastila } from '../../components/ui/Pastila'
import { StareLista } from '../../components/ui/StareLista'
import { useConfirmare } from '../../components/ui/Confirmare'
import { ClientEditModal } from '../../components/clienti/ClientEditModal'
import { dataScurta, eroare, esteRestant, lei } from './comun'
import { FilaPrezentare } from './FilaPrezentare'
import { FilaFacturi, FilaPreturi, FilaPuncte, FilaVanzari } from './FileDate'
import { FilaActe, FilaPoze } from './FilaActe'
import { etichetaSellIn } from '@b4l/shared'

/*
  Fișa clientului (macheta aprobata de Madalin, 20.10.2026). Sus: cine e si cele sase cifre care
  conteaza (Attio / HubSpot: rezumatul intai); dedesubt, filele. Prezentarea se deschide pe „De
  rezolvat" si pe cronologie (Veeva: istoria clientului e vederea lui 360).
*/

export interface Fisa {
  client: {
    id: number
    nume: string
    cif: string | null
    grup: string | null
    agent: string | null
    adresa: string | null
    oras: string | null
    termenPlata: number | null
    modPlata: string
    persoanaContact: string | null
    telefon: string | null
    email: string | null
    contNational: boolean
    activ: boolean
    managerCont: string | null
    observatii: string | null
  }
  cifre: {
    deIncasat: number
    restant: number | null
    zileIntarziere: number | null
    sellIn: number
    target: number
    pctTarget: number | null
    ultimaComanda: string | null
    facturiDeschise: number
    sellout: { pct: number; lipsa: number } | null
  } | null
  deRezolvat: {
    pesteTermen: { docNo: string; ramas: number; scadenta: string | null; zile: number | null }[]
    pesteTermenMaiMulte: number
    scadente7: { count: number; suma: number }
    acteExpira: { id: number; titlu: string; fel: string; valabilPana: string }[]
  }
  acte: { active: number; expira: number }
  puncte: {
    id: number
    branchName: string
    address: string | null
    city: string | null
    state: string | null
    person: string | null
    tel: string | null
    email: string | null
  }[]
}

const FILE = [
  ['prezentare', 'Prezentare'],
  ['facturi', 'Facturi și încasări'],
  ['vanzari', 'Sell-in / Sell-out'],
  ['preturi', 'Prețuri'],
  ['acte', 'Acte'],
  ['poze', 'Poze și expuneri'],
  ['puncte', 'Puncte de lucru și contacte'],
] as const
export type Fila = (typeof FILE)[number][0]

export function ClientPage() {
  const id = Number(useParams().id)
  const [param, setParam] = useSearchParams()
  const fila = (FILE.find(([k]) => k === param.get('fila'))?.[0] ?? 'prezentare') as Fila
  const alege = (f: Fila) => {
    const p = new URLSearchParams(param)
    if (f === 'prezentare') p.delete('fila')
    else p.set('fila', f)
    setParam(p, { replace: true })
  }
  const permisiuni = useAuthStore((s) => s.permissions)
  const eAdmin = useAuthStore((s) => s.user?.role === 'admin')
  const poateActe = permisiuni.includes('clienti.acte:write')
  const poateEdita = permisiuni.includes('orders:write')
  const poatePdf = permisiuni.includes('operational.agenti:read')
  const [editez, setEditez] = useState(false)
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirma } = useConfirmare()

  const q = useQuery<Fisa>({
    queryKey: ['clienti', 'fisa', id],
    queryFn: async () => (await api.get<Fisa>(`/clienti/${id}`, { timeout: 120_000 })).data,
    enabled: Number.isFinite(id) && id > 0,
  })
  const f = q.data

  const sterge = useMutation({
    mutationFn: async () => api.delete(`/orders/clients/${id}`),
    onSuccess: () => {
      toast.success('Client șters.')
      void qc.invalidateQueries({ queryKey: ['clienti'] })
      navigate('/clienti')
    },
    onError: (e) => toast.error(eroare(e, 'Clientul nu s-a putut șterge.')),
  })

  return (
    <div className="grid gap-4">
      <Link
        to="/clienti"
        className="inline-flex items-center gap-1.5 text-corp text-b4l-blue-dark hover:underline"
      >
        <ArrowLeft size={15} aria-hidden="true" /> Clienții mei
      </Link>

      <StareLista interogare={q} gol={false} mesajGol="">
        {f && (
          <>
            <Antet
              f={f}
              poateEdita={poateEdita}
              poateActe={poateActe}
              eAdmin={eAdmin}
              laEditare={() => setEditez(true)}
              laActNou={() => alege('acte')}
              laStergere={async () => {
                if (
                  await confirma({
                    titlu: `Ștergi clientul „${f.client.nume}”?`,
                    mesaj:
                      'Se poate șterge doar dacă nu are comenzi, acte sau activități. Din ExpertAccounts nu se șterge nimic.',
                    buton: 'Șterge clientul',
                    pericol: true,
                  })
                )
                  sterge.mutate()
              }}
            />
            <Cifre f={f} laFila={alege} />

            <nav
              role="tablist"
              aria-label="Fișa clientului"
              className="flex gap-1 overflow-x-auto border-b border-b4l-border"
            >
              {FILE.map(([k, et]) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={fila === k}
                  onClick={() => alege(k)}
                  className={`whitespace-nowrap px-3.5 py-2.5 text-corp ${fila === k ? 'border-b-2 border-b4l-blue font-semibold text-b4l-text' : 'text-b4l-text-muted hover:text-b4l-text'}`}
                >
                  {et}
                  {k === 'acte' && f.acte.active > 0 ? ` · ${f.acte.active}` : ''}
                  {k === 'puncte' && f.puncte.length > 0 ? ` · ${f.puncte.length}` : ''}
                </button>
              ))}
            </nav>

            {fila === 'prezentare' && (
              <FilaPrezentare f={f} poateNota={poateActe} poatePdf={poatePdf} laFila={alege} />
            )}
            {fila === 'facturi' && <FilaFacturi id={id} poatePdf={poatePdf} />}
            {fila === 'vanzari' && <FilaVanzari id={id} />}
            {fila === 'preturi' && <FilaPreturi id={id} />}
            {fila === 'acte' && <FilaActe id={id} puncte={f.puncte} poateScrie={poateActe} />}
            {fila === 'poze' && <FilaPoze id={id} puncte={f.puncte} poateScrie={poateActe} />}
            {fila === 'puncte' && (
              <FilaPuncte f={f} poateEdita={poateEdita} laEditare={() => setEditez(true)} />
            )}
          </>
        )}
      </StareLista>

      {editez && (
        <ClientEditModal
          clientId={id}
          onClose={() => {
            setEditez(false)
            void qc.invalidateQueries({ queryKey: ['clienti', 'fisa', id] })
          }}
        />
      )}
    </div>
  )
}

function Antet({
  f,
  poateEdita,
  poateActe,
  eAdmin,
  laEditare,
  laActNou,
  laStergere,
}: {
  f: Fisa
  poateEdita: boolean
  poateActe: boolean
  eAdmin: boolean
  laEditare: () => void
  laActNou: () => void
  laStergere: () => void
}) {
  const c = f.client
  const z = f.cifre?.zileIntarziere ?? 0
  const restant = esteRestant({ restant: f.cifre?.restant ?? null })
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="grid gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-[26px] font-bold leading-tight text-b4l-text">{c.nume}</h1>
          {c.grup && <Pastila ton="albastru">{c.grup}</Pastila>}
          {c.contNational && <Pastila ton="mov">cont național</Pastila>}
          {!c.activ && <Pastila ton="gri">inactiv</Pastila>}
          {restant && <Pastila ton="rosu">{z > 0 ? `restanță ${z} zile` : 'restanță'}</Pastila>}
        </div>
        <p className="text-corp text-b4l-text-muted">
          {[
            c.cif ? `CIF ${c.cif}` : null,
            c.adresa,
            f.puncte.length
              ? `${f.puncte.length} ${f.puncte.length === 1 ? 'punct de lucru' : 'puncte de lucru'}`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <p className="text-corp text-b4l-text-muted">
          Agent <b className="font-semibold text-b4l-text">{c.agent ?? '—'}</b>
          {c.termenPlata != null && ` · termen de plată ${c.termenPlata} zile`}
          {` · ${c.modPlata === 'PE_MASURA_VANZARII' ? 'plătește pe măsura vânzării' : 'plătește la scadență'}`}
          {c.persoanaContact && ` · contact ${c.persoanaContact}`}
          {c.telefon && `, ${c.telefon}`}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {poateEdita && (
          <Button variant="secondary" onClick={laEditare}>
            Editează datele
          </Button>
        )}
        {poateActe && (
          <Button variant="secondary" onClick={laActNou}>
            Adaugă act sau poză
          </Button>
        )}
        {poateEdita && (
          <Link
            to={`/operational/comenzi/nou?clientId=${c.id}`}
            className="rounded-caseta bg-b4l-blue px-3.5 py-2 text-corp font-semibold text-white hover:bg-b4l-blue-dark"
          >
            + Comandă nouă
          </Link>
        )}
        {eAdmin && (
          <Button variant="secondary" onClick={laStergere}>
            Șterge
          </Button>
        )}
      </div>
    </header>
  )
}

function Cifre({ f, laFila }: { f: Fisa; laFila: (x: Fila) => void }) {
  const c = f.cifre
  const z = c?.zileIntarziere ?? 0
  const restant = esteRestant({ restant: c?.restant ?? null })
  const carte = 'grid gap-0.5 rounded-xl border px-3.5 py-3 text-left'
  return (
    <section
      aria-label="Pe scurt"
      className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(170px,1fr))]"
    >
      <button
        type="button"
        onClick={() => laFila('facturi')}
        className={`${carte} border-b4l-border bg-b4l-surface hover:border-b4l-blue`}
      >
        <span className="text-mic text-b4l-text-muted">De încasat</span>
        <span className="text-[20px] font-bold text-b4l-text">{lei(c?.deIncasat)}</span>
        <span className="text-mic text-b4l-text-muted">
          {c ? `${c.facturiDeschise} facturi deschise` : 'fără date din EA'}
        </span>
      </button>
      <button
        type="button"
        onClick={() => laFila('facturi')}
        className={`${carte} ${restant ? 'border-b4l-red bg-b4l-red-light' : 'border-b4l-border bg-b4l-surface'} hover:border-b4l-blue`}
      >
        <span className={`text-mic ${restant ? 'text-b4l-red-dark' : 'text-b4l-text-muted'}`}>
          Restant
        </span>
        <span
          className={`text-[20px] font-bold ${restant ? 'text-b4l-red-dark' : 'text-b4l-text'}`}
        >
          {restant ? lei(c?.restant) : 'nimic'}
        </span>
        <span
          className={`text-mic ${restant ? 'font-semibold text-b4l-red-dark' : 'text-b4l-text-muted'}`}
        >
          {restant
            ? z > 0
              ? `cea mai veche: ${z} zile peste termen`
              : 'peste termen'
            : 'toate facturile în termen'}
        </span>
      </button>
      <button
        type="button"
        onClick={() => laFila('vanzari')}
        className={`${carte} border-b4l-border bg-b4l-surface hover:border-b4l-blue`}
      >
        <span className="text-mic text-b4l-text-muted">{etichetaSellIn()}</span>
        <span className="text-[20px] font-bold text-b4l-text">{lei(c?.sellIn)}</span>
        <span className="text-mic text-b4l-text-muted">
          {c?.pctTarget != null ? `${Math.round(c.pctTarget)}% din target` : 'fără target'}
        </span>
      </button>
      <button
        type="button"
        onClick={() => laFila('vanzari')}
        className={`${carte} ${(c?.sellout?.lipsa ?? 0) > 0 ? 'border-b4l-amber bg-b4l-amber-light' : 'border-b4l-border bg-b4l-surface'} hover:border-b4l-blue`}
      >
        <span className="text-mic text-b4l-text-muted">Sell-out</span>
        <span className="text-[20px] font-bold text-b4l-text">
          {c?.sellout ? `${c.sellout.pct}%` : '—'}
        </span>
        <span className="text-mic text-b4l-text-muted">
          {!c?.sellout
            ? 'nu trimite rapoarte'
            : c.sellout.lipsa > 0
              ? `lipsesc ${c.sellout.lipsa} rapoarte`
              : 'din ce a cumpărat, vândut'}
        </span>
      </button>
      <div className={`${carte} border-b4l-border bg-b4l-surface`}>
        <span className="text-mic text-b4l-text-muted">Ultima comandă</span>
        <span className="text-[20px] font-bold text-b4l-text">{dataScurta(c?.ultimaComanda)}</span>
        <span className="text-mic text-b4l-text-muted">
          {c?.ultimaComanda ? 'din CRM, din ERP sau factura' : 'nicio comandă'}
        </span>
      </div>
      <button
        type="button"
        onClick={() => laFila('acte')}
        className={`${carte} ${f.acte.expira > 0 ? 'border-b4l-amber bg-b4l-amber-light' : 'border-b4l-border bg-b4l-surface'} hover:border-b4l-blue`}
      >
        <span className="text-mic text-b4l-text-muted">Acte</span>
        <span className="text-[20px] font-bold text-b4l-text">{f.acte.active} active</span>
        <span className="text-mic text-b4l-text-muted">
          {f.acte.expira > 0
            ? `${f.acte.expira} expiră în 30 de zile`
            : f.acte.active
              ? 'toate în termen'
              : 'niciun act adăugat'}
        </span>
      </button>
    </section>
  )
}

/** Deschide un fisier al clientului intr-o fila noua (trece prin autentificarea CRM-ului). */
export async function deschideFisier(clientId: number, actId: number) {
  try {
    const r = await api.get(`/clienti/${clientId}/acte/${actId}/continut`, {
      responseType: 'blob',
      timeout: 60_000,
    })
    window.open(URL.createObjectURL(r.data as Blob), '_blank', 'noopener')
  } catch {
    toast.error('Fișierul nu se poate deschide.')
  }
}

/** Fisa de cont / soldul pe facturi, PDF-urile Tabloului de bord. */
export function descarcaPdf(cale: string, params: Record<string, unknown>, nume: string) {
  downloadFile(cale, params, nume).catch((e: Error) =>
    toast.error(e.message || 'PDF-ul nu s-a putut descărca.'),
  )
}
